import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PaymentSuccess from "../components/payments/PaymentSuccess";
import PaymentFailed from "../components/payments/PaymentFailed";
import { pollPaymentStatus } from "../lib/payment_helpers";
import { createPaymentOrder, storePaymentSession } from "../lib/payment_flow_manager";
import { signAgreement } from "../lib/agreements";

const PENDING_SIGNATURE_KEY = "orello_pending_signature";

/**
 * PaymentStatus page — Cashfree return URL lands here.
 * URL: /payment/status?order_id=orello_XXXXX
 *
 * Handles sequential payments for tenants (agreement fee → protection fee → deposit)
 * Single payment for landlords (agreement fee only)
 *
 * Also handles the pay-then-sign flow: if the user paid their agreement fee share
 * from the signing wizard, a pending signature was stashed in sessionStorage before
 * redirecting here. Once payment is confirmed, that signature is submitted
 * automatically so the user doesn't have to sign twice.
 */
export default function PaymentStatus() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || sessionStorage.getItem("orello_payment_order");
  const rentalId = sessionStorage.getItem("orello_payment_rental");
  const currentStep = parseInt(sessionStorage.getItem("orello_payment_step")) || 1;
  const totalSteps = parseInt(sessionStorage.getItem("orello_payment_total_steps")) || 1;
  const paymentBreakdownStr = sessionStorage.getItem("orello_payment_breakdown");
  const agreementId = sessionStorage.getItem("orello_agreement_id");

  const [status, setStatus] = useState("polling"); // "polling"|"success"|"failed"|"timeout"
  const [payment, setPayment] = useState(null);
  const [processingNextPayment, setProcessingNextPayment] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);

  // Captured once at mount, before we potentially clear the key — tells us whether
  // this payment was for a pay-then-sign flow so we can render the right success UI.
  const [isSignaturePayment] = useState(() => !!sessionStorage.getItem(PENDING_SIGNATURE_KEY));
  const [signatureSubmission, setSignatureSubmission] = useState("idle"); // idle|submitting|done|error
  const [signatureError, setSignatureError] = useState(null);
  const [signatureRentalId, setSignatureRentalId] = useState(null);

  useEffect(() => {
    if (paymentBreakdownStr) {
      setPaymentBreakdown(JSON.parse(paymentBreakdownStr));
    }
  }, [paymentBreakdownStr]);

  useEffect(() => {
    if (!orderId) {
      setStatus("failed");
      return;
    }

    // Check if we already confirmed this payment (from earlier polling)
    const cachedPayment = sessionStorage.getItem(`payment_${orderId}_cached`);
    if (cachedPayment) {
      try {
        const cached = JSON.parse(cachedPayment);
        setPayment(cached);
        setStatus(cached.status === "success" ? "success" : "failed");
        return;
      } catch (e) {
        // Invalid cache, re-poll
      }
    }

    // Poll for payment confirmation
    pollPaymentStatus(orderId, (update) => setPayment(update)).then((result) => {
      setPayment(result);
      // Cache the result so if user refreshes, we don't re-poll
      sessionStorage.setItem(`payment_${orderId}_cached`, JSON.stringify(result));
      setStatus(result.status === "success" ? "success" : result.status === "timeout" ? "timeout" : "failed");
    });
  }, [orderId]);

  // Prevent back navigation on entire payment page using sentinel state
  useEffect(() => {
    // Push a sentinel state that we can detect
    window.history.pushState({ preventBack: true }, document.title);

    const handlePopState = (e) => {
      // Always push sentinel state to prevent going back
      window.history.pushState({ preventBack: true }, document.title);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Once payment is confirmed, submit any pending signature stashed before redirect.
  useEffect(() => {
    if (status !== "success") return;
    const pendingRaw = sessionStorage.getItem(PENDING_SIGNATURE_KEY);
    if (!pendingRaw) return;

    (async () => {
      setSignatureSubmission("submitting");
      try {
        const pending = JSON.parse(pendingRaw);
        await signAgreement(pending.agreementId, pending.payload);
        sessionStorage.removeItem(PENDING_SIGNATURE_KEY);
        setSignatureRentalId(pending.rentalId);
        setSignatureSubmission("done");
        const target = pending.rentalId ? `/app/rental/${pending.rentalId}` : "/app";
        setTimeout(() => { window.location.href = target; }, 1800);
      } catch (err) {
        console.error("Failed to submit signature after payment:", err);
        setSignatureError(err.response?.data?.detail || err.message || "Failed to submit your signature.");
        setSignatureSubmission("error");
      }
    })();
  }, [status]);

  // Handle proceeding to next payment
  const handleNextPayment = async () => {
    if (!paymentBreakdown || currentStep >= totalSteps) {
      // All payments done - redirect to rental or home
      const target = rentalId ? `/app/rental/${rentalId}` : "/app";
      window.location.href = target;
      return;
    }

    setProcessingNextPayment(true);
    try {
      const nextPayment = paymentBreakdown.payments[currentStep]; // currentStep is 1-indexed, array is 0-indexed

      if (!nextPayment) {
        // No more payments - redirect to rental or home
        const target = rentalId ? `/app/rental/${rentalId}` : "/app";
        window.location.href = target;
        return;
      }

      const paymentResult = await createPaymentOrder(nextPayment, agreementId, rentalId);

      if (paymentResult.success) {
        storePaymentSession(
          paymentResult.orderId,
          rentalId,
          currentStep + 1,
          totalSteps,
          nextPayment.type
        );

        window.location.href = paymentResult.paymentUrl;
      } else {
        alert(`Failed to initiate next payment: ${paymentResult.error}`);
        setProcessingNextPayment(false);
      }
    } catch (error) {
      console.error("Error initiating next payment:", error);
      alert("Failed to proceed to next payment. Please try again.");
      setProcessingNextPayment(false);
    }
  };

  // Determine if we should show "next payment" button
  const hasMorePayments = currentStep < totalSteps;

  return (
    <div className="min-h-screen" style={{ background: "#080808", color: "#F5F5F5" }}>
      <Header />

      <main className="max-w-2xl mx-auto px-6 py-16">
        {status === "polling" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-16 h-16 rounded-full bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center mb-6">
              <span className="animate-spin rounded-full h-8 w-8 border-3 border-indigo-500 border-t-transparent" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Confirming your payment…
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xs mb-4">
              Please don't close this tab. We're checking with Cashfree — this usually takes a few seconds.
            </p>

            {/* Countdown Timer */}
            {payment?.seconds_remaining !== undefined && (
              <div className="text-sm text-gray-400">
                Timeout in <span className="font-semibold text-indigo-500">{payment.seconds_remaining}s</span>
              </div>
            )}

            {/* Info Box */}
            <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4 max-w-sm">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                💡 <strong>No amount charged yet.</strong> Confirmation is in progress. If this times out, your account will not be charged.
              </p>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            {/* Step Progress */}
            {totalSteps > 1 && (
              <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
                <div className="text-sm text-gray-400 mb-3">Payment Progress</div>
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalSteps }).map((_, i) => (
                    <React.Fragment key={i}>
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                          i + 1 <= currentStep
                            ? "bg-[#F97316] text-white"
                            : "bg-gray-800 text-gray-400"
                        }`}
                      >
                        {i + 1 < currentStep ? "✓" : i + 1}
                      </div>
                      {i < totalSteps - 1 && (
                        <div
                          className={`flex-1 h-1 ${
                            i + 1 < currentStep ? "bg-[#F97316]" : "bg-gray-800"
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="text-xs text-gray-400 mt-3">
                  Step {currentStep} of {totalSteps}
                </div>
              </div>
            )}

            {/* Success Message */}
            <PaymentSuccess
              orderId={orderId}
              rentalId={payment?.rental_id || rentalId}
              amount={payment?.amount}
              depositAmount={payment?.deposit_amount}
            />

            {/* Pay-then-sign flow: submit the stashed signature now that payment is confirmed */}
            {isSignaturePayment && (
              <div className="bg-gradient-to-r from-[#F97316]/10 to-orange-500/10 rounded-lg p-6 border border-[#F97316]/30">
                {signatureSubmission !== "error" ? (
                  <>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {signatureSubmission === "done" ? "✅ Signed & Paid" : "✍️ Submitting your signature…"}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {signatureSubmission === "done"
                        ? "Your payment is confirmed and your signature has been recorded. Redirecting…"
                        : "Payment confirmed. Finalizing your signature on the agreement — this only takes a moment."}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-white mb-2">⚠️ Payment succeeded, signature failed</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {signatureError} Your payment went through, but we couldn't submit your signature automatically.
                      Please go back to the agreement and sign again — you won't be charged twice.
                    </p>
                    <button
                      onClick={() => {
                        window.location.href = signatureRentalId ? `/app/rental/${signatureRentalId}` : rentalId ? `/app/rental/${rentalId}` : "/app";
                      }}
                      className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 rounded-lg transition"
                    >
                      Return to Agreement
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Next Payment or Complete (deposit / legacy multi-step payment flows) */}
            {!isSignaturePayment && hasMorePayments && (
              <div className="bg-gradient-to-r from-[#F97316]/10 to-orange-500/10 rounded-lg p-6 border border-[#F97316]/30">
                <h3 className="text-lg font-semibold text-white mb-2">
                  ✅ Step {currentStep} Complete
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Payment confirmed! Ready for the next step?
                </p>

                {/* Show next payment details */}
                {paymentBreakdown && paymentBreakdown.payments[currentStep] && (
                  <div className="bg-gray-900/50 rounded p-3 mb-4 border border-gray-800">
                    <div className="text-sm text-gray-400">Next Payment</div>
                    <div className="text-lg font-semibold text-white mt-1">
                      {paymentBreakdown.payments[currentStep].name}
                    </div>
                    <div className="text-sm text-[#F97316] mt-1">
                      ₹{(paymentBreakdown.payments[currentStep].amount / 100).toLocaleString("en-IN")}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleNextPayment}
                  disabled={processingNextPayment}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 rounded-lg disabled:opacity-50 transition"
                >
                  {processingNextPayment ? "Processing…" : "Proceed to Next Payment"}
                </button>
              </div>
            )}

            {/* All payments done (deposit / legacy multi-step payment flows) */}
            {!isSignaturePayment && !hasMorePayments && (
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg p-6 border border-green-500/30">
                <h3 className="text-lg font-semibold text-white mb-2">
                  🎉 All Payments Complete
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  {rentalId
                    ? "Your agreement is now active. You can proceed to the rental dashboard."
                    : "Your payment is complete. Your signature will be submitted automatically."}
                </p>
                <button
                  onClick={() => {
                    const target = rentalId ? `/app/rental/${rentalId}` : "/app";
                    window.location.href = target;
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition"
                >
                  {rentalId ? "Go to Rental Dashboard" : "Go to Dashboard"}
                </button>
              </div>
            )}
          </div>
        )}

        {(status === "failed" || status === "timeout") && (
          <PaymentFailed
            orderId={orderId}
            rentalId={payment?.rental_id || rentalId}
            reason={
              status === "timeout"
                ? "Payment verification timed out. If money was deducted, contact support."
                : "The payment was not completed."
            }
          />
        )}
      </main>

      <Footer />
    </div>
  );
}
